// @ts-strict-ignore
/* eslint-disable no-continue */
import { ruleType } from 'flow/service/rules';
import { booleanRuleStrings } from '../../constants';
import type { RuleObject } from '../../types';
import { generatePublicId } from 'flow/store/engine/utils';

type BooleanRule = string;
type BooleanOperatorString = 'or' | 'and';
type BooleanOperator = '|' | '&';
type BooleanRuleItem = `${number}` | BooleanOperator | BooleanRuleItem[];
type BooleanRuleGroup = BooleanRuleItem[];

export type IdToPublicIdTransformationMap = {
  [legacyId: string]: string;
};

export function transformBooleanRule(args: {
  booleanRule: BooleanRule;
  legacyToTransformedThresholdIdMap: IdToPublicIdTransformationMap;
}) {
  const { booleanRule, legacyToTransformedThresholdIdMap } = args;


  const booleanRuleGroup = transformBooleanRuleToRuleGroup(booleanRule);
  return transformBooleanRuleGroupIntoRule({
    completeRuleGroup: booleanRuleGroup,
    legacyToTransformedThresholdIdMap,
  });
}

/**
 * Converts a boolean rule string into a BooleanRuleGroup. Parenthesis are converted
 * to nested arrays.
 * (1|(2&3)) -> ["1", "|", ["2", "&", "3"]]
 *
 * (this function borrowed from rules.ts with some minor tweaks)
 */
function transformBooleanRuleToRuleGroup(rule: string): BooleanRuleGroup {
  // this splits boolean rule string into array by using (, ), |, & string
  const ruleLogic = rule.match(/(\d+|\||&|\(|\))/g);
  // Keep track of group at each level in dict with groupLevel key, groupArray value
  // nestedGroups[1] will contain entire nested array at finish
  const nestedGroups = { 0: [] };
  let groupLevel = 0;
  ruleLogic.forEach(value => {
    if (value === booleanRuleStrings.GROUP_START) {
      groupLevel += 1;
      nestedGroups[groupLevel] = [];
    } else if (value === booleanRuleStrings.GROUP_END) {
      groupLevel -= 1;
      nestedGroups[groupLevel].push(nestedGroups[groupLevel + 1]);
    } else {
      nestedGroups[groupLevel].push(value);
    }
  });
  return nestedGroups[1];
}

/**
 * Main transformation logic.
 * Turns a BooleanRuleGroup into a Rules object, using the legacy id map as a reference
 * for the ids
 */
function transformBooleanRuleGroupIntoRule(args: {
  completeRuleGroup: BooleanRuleGroup;
  legacyToTransformedThresholdIdMap: IdToPublicIdTransformationMap;
}) {
  const { completeRuleGroup, legacyToTransformedThresholdIdMap } = args;

  if (isEmptyGroup(completeRuleGroup)) {
    return {};
  }

  if (isSingleNumericValueGroup(completeRuleGroup)) {
    const thresholdId = extractSingleNumericValueFromGroup(completeRuleGroup);
    const thresholdPublicId = legacyToTransformedThresholdIdMap[thresholdId];
    return {
      publicId: generatePublicId(ruleType.group),
      and: [
        buildThresholdIdRuleEntry(thresholdPublicId),
      ],
    };
  }

  /**
   * recursive function that builds the rules for a given group level
   */
  function buildRule(group: BooleanRuleGroup, isAmbiguous: boolean = false) {
    // build a result object for the group using the main operator as the key
    const groupBooleanOperator = synthesizePrimaryOperatorFromBooleanRuleGroup(group);
    const operatorString = getBooleanOperatorAsBooleanOperatorString(groupBooleanOperator);
    const result: RuleObject = {
      [operatorString]: [],
      ...(isAmbiguous && { ambiguous: true }),
      publicId: generatePublicId(ruleType.group),
    };

    /**
     * traverse the group using depth-first search (DPS). this means that we recurse as soon as we
     * find a nested group. a `while` loop is used because we need fine control of the iteration
     * index in different branches of logic.
     */
    let i = 0;
    while (i < group.length) {
      const value = group.at(i);

      if (isNestedGroup(value)) {
        // recurse on the nested group and store the result
        const nestedResult = buildRule(value);
        result[operatorString].push(nestedResult);
        i += 1;
        continue;
      }

      // base case - build the rule entry when there is no more recursion left to do
      if (isNumericRuleValue(value)) {
        const thresholdPublicId = legacyToTransformedThresholdIdMap[value];
        const entry = buildThresholdIdRuleEntry(thresholdPublicId);
        result[operatorString].push(entry);
        i += 1;
        continue;
      }


      if (isBooleanOperator(value)) {
        // we only look for ambiguity when visiting an operator
        const ambiguousSearchResult = searchForAmbiguousOperatorsInGroup({
          group,
          startIdx: i,
          baseOperator: groupBooleanOperator,
        });

        // if none is found, move to the next value
        if (!ambiguousSearchResult.foundAmbiguousOperator) {
          i += 1;
          continue;
        } else {
          const { firstAmbiguousOperatorIdx, lastAmbiguousOperatorIdx } = ambiguousSearchResult;
          // grab a subgroup of the items in ambiguity
          const ambiguousSubgroup = getSubgroupOfItemsWithinAmbiguity({
            firstAmbiguousOperatorIdx,
            group,
            lastAmbiguousOperatorIdx,
          });
          // recurse on the subgroup and mark it as ambiguous, then store the result
          const ambiguousResult = buildRule(ambiguousSubgroup, true);
          result[operatorString].push(ambiguousResult);
          /**
           * after the subgroup is evaluated, we no longer need to revisit its items so we move
           * the iteration index to the value after the ambiguous subgroup
           */
          i += ambiguousSubgroup.length + 1;
        }
      }
    }
    return result;
  }

  const rules = buildRule(completeRuleGroup);
  return rules;
}

/**
 * grabs the primary (first) operator in a group. if none are found, returns a default
 */
function synthesizePrimaryOperatorFromBooleanRuleGroup(group: BooleanRuleGroup): BooleanOperator {
  const defaultOperator: BooleanOperator = '&';

  const firstOperator = group.find(item => item === '&' || item === '|');

  return firstOperator ?? defaultOperator;
}

function getBooleanOperatorAsBooleanOperatorString(
  booleanOperator: BooleanOperator,
): BooleanOperatorString {
  return booleanOperator === '&' ? 'and' : 'or';
}


/**
 * search for ambiguity using the baseOperator as the reference.
 * if found, return the indices of the first and last ambiguous operators
 */
function searchForAmbiguousOperatorsInGroup(args: {
  group: BooleanRuleGroup;
  baseOperator: BooleanOperator;
  startIdx: number;
}) {
  const { group, startIdx, baseOperator } = args;
  let currentOperatorIdx = startIdx;

  let isNextOperatorAmbiguous = false;
  do {
    // an operator will always be 2 positions away, but it might no exist (out of bounds)
    const nextOperatorIndex = currentOperatorIdx + 2;
    const nextOperator = group.at(nextOperatorIndex);

    isNextOperatorAmbiguous = Boolean(nextOperator) && baseOperator !== nextOperator;
    if (isNextOperatorAmbiguous) {
      /**
       * if the next operator is ambiguous, we move to its index and continue the search.
       * we do this to find the last ambiguous operator
       */
      currentOperatorIdx = nextOperatorIndex;
    }
    // stop when no longer finding ambiguous operators
  } while (isNextOperatorAmbiguous);


  // these will be different if any ambiguous operators were found (currentOperatorIdx moved)
  const foundAmbiguousOperator = startIdx !== currentOperatorIdx;
  return {
    foundAmbiguousOperator,
    // first ambiguous operator is always 2 positions after the starting operator
    firstAmbiguousOperatorIdx: foundAmbiguousOperator ? startIdx + 2 : null,
    // last ambiguous operator is where the search ended
    lastAmbiguousOperatorIdx: foundAmbiguousOperator ? currentOperatorIdx : null,
  };
}

/**
 * grabs all items that are besides the first and last ambiguous operator
 */
function getSubgroupOfItemsWithinAmbiguity(args: {
  group: BooleanRuleGroup;
  firstAmbiguousOperatorIdx: number;
  lastAmbiguousOperatorIdx: number;
}) {
  const { firstAmbiguousOperatorIdx, group, lastAmbiguousOperatorIdx } = args;

  /**
   * the indices point to the operators themselves, but we need to grab the subgroup
   * made up of all values in-between the operators
   */
  return group.slice(
    // grab the item that's right before the first ambiguous operator
    firstAmbiguousOperatorIdx - 1,
    // grab the item that's right after the last ambiguous operator plus 1, as .slice is exclusive
    lastAmbiguousOperatorIdx + 1 + 1,
  );
}

function extractSingleNumericValueFromGroup(group: BooleanRuleGroup) {
  if (!isSingleNumericValueGroup(group)) {
    throw new Error(`is not single value group - ${group}`);
  }

  return Number(group[0]);
}

function isEmptyGroup(group: BooleanRuleGroup) {
  return group.length === 0;
}

function isSingleNumericValueGroup(group: BooleanRuleGroup) {
  const item = group.at(0);
  /**
   * JS evaluates `Number([X])` to `X`, so we guard against that case.
   * this happens on nested groups that have only 1 value. for example: ((1)). here, the outer
   * group should not be deemed single-numeric, but the inner one should
   */
  if (isNestedGroup(item)) {
    return false;
  }

  return group.length === 1 && isNumericRuleValue(item);
}

function isNestedGroup(ruleItem: BooleanRuleItem): ruleItem is BooleanRuleItem[] {
  return Array.isArray(ruleItem);
}

function isNumericRuleValue(ruleItem: BooleanRuleItem): ruleItem is `${number}` {
  return !isNaN(Number(ruleItem));
}

function isBooleanOperator(ruleItem: BooleanRuleItem): ruleItem is BooleanOperator {
  return ruleItem === '&' || ruleItem === '|';
}

function buildThresholdIdRuleEntry(thresholdPublicId: string) {
  return { id: thresholdPublicId };
}
